import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './BPlusSignal.module.css';

// Small dependency-free SVG line chart: responsive width, optional log scale,
// background bands (e.g. "holding TQQQ") and a hover tooltip.
// Points are spaced by trading day, so weekends and holidays leave no gaps.
// With `timeScale`, points are placed by calendar date instead, for data
// whose spacing changes (weekly readings followed by daily ones).

const MARGIN = { top: 12, right: 16, bottom: 28, left: 60 };

// Index of the time in a sorted array closest to t.
function nearestIndex(times, t) {
    let lo = 0;
    let hi = times.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (times[mid] <= t) lo = mid;
        else hi = mid;
    }
    return Math.abs(times[hi] - t) < Math.abs(times[lo] - t) ? hi : lo;
}

function niceStep(range, count) {
    const raw = range / Math.max(1, count);
    const mag = 10 ** Math.floor(Math.log10(raw));
    const norm = raw / mag;
    const step = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
    return step * mag;
}

function linearTicks(lo, hi, count = 5) {
    const step = niceStep(hi - lo, count);
    const ticks = [];
    for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) ticks.push(v);
    return ticks;
}

function logTicks(lo, hi) {
    const ticks = [];
    for (let k = Math.floor(Math.log10(lo)); k <= Math.ceil(Math.log10(hi)); k += 1) {
        [1, 2, 5].forEach((m) => {
            const v = m * 10 ** k;
            if (v >= lo && v <= hi) ticks.push(v);
        });
    }
    // A narrow range (e.g. $1 → $2.7) gets only one or two 1-2-5 ticks: use even steps instead.
    return ticks.length >= 3 ? ticks : linearTicks(lo, hi, 4);
}

function buildPath(data, key, x, y) {
    let d = '';
    let pen = false;
    data.forEach((row, i) => {
        const v = row[key];
        if (v == null || !Number.isFinite(v)) {
            pen = false;
            return;
        }
        d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
        pen = true;
    });
    return d;
}

function LineChart({
    data,
    series,
    bands = [],
    log = false,
    timeScale = false,
    height = 340,
    yFormat = (v) => v.toFixed(0),
    xFormat = (d) => d.slice(0, 7),
    tooltipExtra,
    ariaLabel,
}) {
    const wrapRef = useRef(null);
    const [width, setWidth] = useState(800);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        setWidth(el.clientWidth || 800);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0].contentRect.width;
            if (w > 0) setWidth(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const n = data.length;
    const iw = Math.max(10, width - MARGIN.left - MARGIN.right);
    const ih = height - MARGIN.top - MARGIN.bottom;

    const [lo, hi] = useMemo(() => {
        let a = Infinity;
        let b = -Infinity;
        data.forEach((row) => series.forEach((s) => {
            const v = row[s.key];
            if (v != null && Number.isFinite(v)) {
                a = Math.min(a, v);
                b = Math.max(b, v);
            }
        }));
        if (!Number.isFinite(a)) return [0, 1];
        if (log) return [a / 1.08, b * 1.08];
        const pad = (b - a) * 0.06 || 1;
        return [a - pad, b + pad];
    }, [data, series, log]);

    const times = useMemo(
        () => (timeScale ? data.map((r) => Date.parse(r.date)) : null),
        [data, timeScale],
    );
    const t0 = times && n ? times[0] : 0;
    const tSpan = times && n ? (times[n - 1] - t0) || 1 : 1;

    const x = (i) => {
        if (n <= 1) return MARGIN.left + iw / 2;
        const f = times ? (times[i] - t0) / tSpan : i / (n - 1);
        return MARGIN.left + f * iw;
    };
    const y = (v) => {
        const f = log
            ? (Math.log(v) - Math.log(lo)) / (Math.log(hi) - Math.log(lo))
            : (v - lo) / (hi - lo);
        return MARGIN.top + ih - f * ih;
    };
    const step = n > 1 ? iw / (n - 1) : iw;
    const yTicks = log ? logTicks(lo, hi) : linearTicks(lo, hi, 5);
    const xCount = Math.max(2, Math.floor(iw / 120));
    let xTicks = [0];
    if (n > 1 && times) {
        // Evenly spaced in time, each labelled with its nearest data point.
        xTicks = [...new Set(Array.from({ length: xCount }, (_, k) => (
            nearestIndex(times, t0 + (k * tSpan) / (xCount - 1))
        )))];
    } else if (n > 1) {
        xTicks = Array.from({ length: xCount }, (_, k) => Math.round((k * (n - 1)) / (xCount - 1)));
    }

    const onMove = (e) => {
        if (!n) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const f = (e.clientX - rect.left - MARGIN.left) / iw;
        const i = times ? nearestIndex(times, t0 + f * tSpan) : Math.round(f * (n - 1));
        setHover(Math.max(0, Math.min(n - 1, i)));
    };

    const row = hover != null ? data[hover] : null;
    const tipLeft = hover != null ? x(hover) : 0;
    const flip = tipLeft > width * 0.6;

    return (
        <div ref={wrapRef} className={styles.chartWrap} style={{ height }}>
            <svg
                width={width}
                height={height}
                role="img"
                aria-label={ariaLabel}
                onMouseMove={onMove}
                onMouseLeave={() => setHover(null)}
                className={styles.chartSvg}
            >
                {bands.map((b) => (
                    <rect
                        key={`${b.from}-${b.color}`}
                        x={Math.max(MARGIN.left, x(b.from) - step / 2)}
                        y={MARGIN.top}
                        width={Math.max(1, Math.min(MARGIN.left + iw, x(b.to) + step / 2) - Math.max(MARGIN.left, x(b.from) - step / 2))}
                        height={ih}
                        fill={b.color}
                    />
                ))}
                {yTicks.map((v) => (
                    <g key={v}>
                        <line x1={MARGIN.left} x2={MARGIN.left + iw} y1={y(v)} y2={y(v)} className={styles.gridLine} />
                        <text x={MARGIN.left - 8} y={y(v)} dy="0.32em" textAnchor="end" className={styles.axisText}>
                            {yFormat(v)}
                        </text>
                    </g>
                ))}
                {xTicks.map((i, k) => (
                    <text
                        key={i}
                        x={x(i)}
                        y={height - 8}
                        textAnchor={k === 0 ? 'start' : k === xTicks.length - 1 ? 'end' : 'middle'}
                        className={styles.axisText}
                    >
                        {xFormat(data[i].date)}
                    </text>
                ))}
                {series.map((s) => (
                    <path
                        key={s.key}
                        d={buildPath(data, s.key, x, y)}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={s.width || 1.5}
                        strokeDasharray={s.dash}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        opacity={s.opacity ?? 1}
                    />
                ))}
                {row && (
                    <g>
                        <line x1={tipLeft} x2={tipLeft} y1={MARGIN.top} y2={MARGIN.top + ih} className={styles.hoverLine} />
                        {series.map((s) => (row[s.key] != null ? (
                            <circle key={s.key} cx={tipLeft} cy={y(row[s.key])} r={3.2} fill={s.color} />
                        ) : null))}
                    </g>
                )}
            </svg>
            {row && (
                <div
                    className={styles.tooltip}
                    style={flip ? { right: width - tipLeft + 12 } : { left: tipLeft + 12 }}
                >
                    <div className={styles.tooltipDate}>{row.date}</div>
                    {series.filter((s) => row[s.key] != null).map((s) => (
                        <div key={s.key} className={styles.tooltipRow}>
                            <span className={styles.swatch} style={{ background: s.color }} />
                            <span>{s.label}</span>
                            <span className={styles.tooltipValue}>{yFormat(row[s.key], true)}</span>
                        </div>
                    ))}
                    {tooltipExtra && tooltipExtra(row)}
                </div>
            )}
        </div>
    );
}

export default LineChart;
